import { useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import type { AdminContentPage, CafePageText } from '@mds/shared/types/content';

import { useSession } from '@/auth/use-session';
import CafePreview from '@/components/cafe/cafe-preview';
import MenuEditor from '@/components/cafe/menu-editor';
import { useAssetUrls } from '@/components/cafe/use-asset-urls';
import GalleryPicker from '@/components/files/gallery-picker';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Field, { inputCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Pager from '@/components/ui/pager';
import { Table, Row, Cell } from '@/components/ui/table';
import { SegmentedToggle } from '@/components/ui/toolbar';
import * as content from '@/content/content.api';
import {
  LIMITS,
  cafeSchema,
  documentOf,
  draftOf,
  invalidAssets,
  isDirty,
  isVersionConflict,
  statusLabel,
  statusTone,
  type CafeDraft,
} from '@/domain/cafe';
import { stamp } from '@mds/shared/lib/dates';
import { useValidation } from '@/lib/validation';

const SLUG = 'cafe' as const;
const HISTORY_PAGE = 10;

/** The two halves of the café content: the page's words and pictures, and the menu. */
type Tab = 'page' | 'menu';

/** Which tab a draft path belongs to, so Save can open the one with the problem. */
const tabOf = (path: string): Tab =>
  path.startsWith('categories') || path === 'page.menuNote' ? 'menu' : 'page';

type Notice = { tone: 'ok' | 'warn' | 'bad'; text: string };

const TONES: Record<Notice['tone'], string> = {
  ok: 'border-brand-edge/35 bg-brand-wash text-brand-strong',
  warn: 'border-warn-edge bg-warn-wash text-warn-text',
  bad: 'border-danger-edge bg-danger-wash text-danger-text',
};

/**
 * The café page and its menu.
 *
 * Saving writes a private draft revision and never touches the public page;
 * publishing makes the saved draft live. Those are separate permissions and
 * separate buttons, and a form with unsaved edits cannot be published — what
 * goes live is always something that was saved and can be looked back at.
 *
 * The page and the menu are two tabs over one draft: one Save, one Publish,
 * one history. A problem on the tab that is not showing switches to it before
 * focus moves, so nothing can block a save from out of sight.
 */
export default function CafePage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const mayManage = can('content.manage');
  const mayPublish = can('content.publish');

  const page = useQuery({ queryKey: ['content', SLUG], queryFn: () => content.getPage(SLUG) });

  const [tab, setTab] = useState<Tab>('page');
  const [reveal, setReveal] = useState<{ path: string; at: number } | null>(null);
  const [draft, setDraft] = useState<CafeDraft | null>(null);
  const [seededEtag, setSeededEtag] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [revisionNumber, setRevisionNumber] = useState<number | null>(null);
  const validation = useValidation(cafeSchema, draft);

  const dirty = draft !== null && page.data ? isDirty(draft, page.data.document) : false;

  // Seeded from each newly loaded copy, unless the form holds unsaved edits —
  // a reload after a conflict must not wipe out what the editor typed.
  if (page.data && page.data.etag !== seededEtag && !dirty) {
    setSeededEtag(page.data.etag);
    setDraft(draftOf(page.data.document));
    validation.reset();
  }

  const history = useQuery({
    queryKey: ['content', SLUG, 'revisions', historyPage],
    queryFn: () => content.listRevisions(SLUG, { page: historyPage, limit: HISTORY_PAGE }),
  });
  const revision = useQuery({
    queryKey: ['content', SLUG, 'revision', revisionNumber],
    queryFn: () => content.getRevision(SLUG, revisionNumber as number),
    enabled: revisionNumber !== null,
  });

  const referenced = useMemo(() => {
    if (!draft) return [];
    return [
      ...(draft.heroImageFileId ? [draft.heroImageFileId] : []),
      ...draft.galleryFileIds,
      ...draft.categories.flatMap((c) =>
        c.items.flatMap((i) => (i.imageFileId ? [i.imageFileId] : [])),
      ),
    ];
  }, [draft]);
  const known = page.data?.assets ?? [];
  const urls = useAssetUrls(referenced, known);
  const revisionUrls = useAssetUrls([], revision.data?.assets ?? []);

  /** Takes a page the server returned as the new saved state, and the form with it. */
  const adopt = (next: AdminContentPage) => {
    queryClient.setQueryData(['content', SLUG], next);
    setSeededEtag(next.etag);
    setDraft(draftOf(next.document));
    validation.reset();
    void queryClient.invalidateQueries({ queryKey: ['content', SLUG, 'revisions'] });
  };

  const run = async (
    action: (etag: string) => Promise<AdminContentPage>,
    done: string,
    fallback: string,
  ) => {
    if (!page.data) return;
    setBusy(true);
    setNotice(null);
    try {
      adopt(await action(page.data.etag));
      setNotice({ tone: 'ok', text: done });
    } catch (e) {
      if (isVersionConflict(e)) {
        // Keep the form exactly as it is and fetch the latest saved state, so
        // the editor can compare and decide rather than lose either side.
        await page.refetch();
        setNotice({
          tone: 'warn',
          text: 'Someone else changed the café page while you were editing. Your edits are still in the form, and the page status above is now the latest. Preview to compare, then save again to keep yours, or discard to take theirs.',
        });
      } else {
        const bad = invalidAssets(e);
        setNotice({
          tone: 'bad',
          text:
            bad.length > 0
              ? `${bad.length} ${bad.length === 1 ? 'picture is' : 'pictures are'} no longer a public, ready image (deleted, made private, or still uploading). Replace ${bad.length === 1 ? 'it' : 'them'} and try again.`
              : e instanceof ApiError
                ? e.message
                : fallback,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * Runs the validation the way Save needs it: the tab holding the first
   * problem is opened first, so the focus `check()` moves lands on screen.
   */
  const checked = () => {
    const first = Object.keys(validation.errors)[0];
    if (first) {
      setTab(tabOf(first));
      setReveal({ path: first, at: Date.now() });
    }
    return validation.check();
  };

  const save = () => {
    if (!draft || !checked()) return;
    const document = documentOf(draft);
    void run(
      (etag) => content.savePage(SLUG, document, etag),
      'Draft saved. The public page has not changed. Publish when it is ready.',
      'That did not save. Try again.',
    );
  };

  const discard = () => {
    if (!page.data) return;
    setDraft(draftOf(page.data.document));
    setSeededEtag(page.data.etag);
    setNotice(null);
    validation.reset();
  };

  if (page.isPending) return <p className="m-0 text-base text-ink-muted">Loading the café page…</p>;
  if (page.isError || !draft)
    return (
      <EmptyState
        title="Could not load the café page"
        hint={
          page.error instanceof ApiError ? page.error.message : 'The server could not be reached.'
        }
      />
    );

  const saved = page.data;
  const readOnly = !mayManage;
  const setText = (patch: Partial<CafePageText>) =>
    setDraft({ ...draft, page: { ...draft.page, ...patch } });

  return (
    <div className="flex flex-col gap-6">
      {/* ------------------------------------------------------------- tabs */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <SegmentedToggle value={tab} options={['page', 'menu'] as const} onChange={setTab} />
        <span className="text-base text-ink-muted">
          {tab === 'page'
            ? 'The words and pictures on the café page.'
            : 'What the café serves and what it costs.'}
        </span>
      </div>

      {/* ------------------------------------------------ status and actions */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-hairline bg-surface px-5 py-4 shadow-card">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <Chip className={'border ' + statusTone(saved.status)}>
              {statusLabel(saved.status)}
            </Chip>
            {dirty && (
              <Chip className="border border-warn-edge bg-warn-wash text-warn-text">
                Unsaved edits
              </Chip>
            )}
          </div>
          <span className="text-base text-ink-muted">
            Draft is revision {saved.draftRevisionNumber}
            {saved.publishedRevisionNumber !== null
              ? ` · revision ${saved.publishedRevisionNumber} is live since ${stamp(saved.publishedAt)}`
              : ' · nothing is live yet'}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="quiet" onClick={() => setPreviewing(true)}>
            Preview
          </Button>
          {mayManage && dirty && (
            <Button variant="quiet" disabled={busy} onClick={discard}>
              Discard edits
            </Button>
          )}
          {mayManage && (
            <Button disabled={busy || !dirty} onClick={save}>
              {busy ? 'Working…' : 'Save draft'}
            </Button>
          )}
          {mayPublish && saved.status !== 'published' && (
            <Button
              variant="mint"
              disabled={busy || dirty}
              onClick={() => {
                if (!checked()) return;
                void run(
                  (etag) => content.publishPage(SLUG, etag),
                  `Revision ${saved.draftRevisionNumber} is live.`,
                  'That did not publish. Try again.',
                );
              }}
            >
              Publish
            </Button>
          )}
          {mayPublish && saved.status !== 'draft' && (
            <Button
              variant="danger"
              disabled={busy}
              onClick={() =>
                void run(
                  (etag) => content.unpublishPage(SLUG, etag),
                  'The café page is offline. The draft and its history are kept.',
                  'That did not unpublish. Try again.',
                )
              }
            >
              Unpublish
            </Button>
          )}
        </div>
      </div>

      {mayPublish && dirty && saved.status !== 'published' && (
        <p className="m-0 text-base text-ink-muted">
          Save the draft first: publishing makes the saved draft live, not the form.
        </p>
      )}
      {!mayManage && (
        <p className="m-0 text-base text-ink-muted">
          You can read the café page but not change it.
        </p>
      )}
      {notice && (
        <p
          role={notice.tone === 'ok' ? 'status' : 'alert'}
          className={'m-0 rounded-xl border px-4 py-3 text-base ' + TONES[notice.tone]}
        >
          {notice.text}
        </p>
      )}

      {/* ---------------------------------------------------------- page text */}
      {tab === 'page' && (
        <Card title="Page">
          <div className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-[1fr_2fr]">
              <Field
                label="Eyebrow"
                hint="The small line above the title."
                {...validation.field('page.eyebrow')}
              >
                <input
                  className={inputCls}
                  value={draft.page.eyebrow}
                  maxLength={80}
                  disabled={readOnly}
                  placeholder="MDS Café"
                  onChange={(e) => setText({ eyebrow: e.target.value })}
                />
              </Field>
              <Field label="Title" required {...validation.field('page.title')}>
                <input
                  className={inputCls}
                  value={draft.page.title}
                  maxLength={160}
                  disabled={readOnly}
                  placeholder="Coffee, conversations and light bites"
                  onChange={(e) => setText({ title: e.target.value })}
                />
              </Field>
            </div>
            <Field
              label="Summary"
              hint="A line or two under the title."
              {...validation.field('page.summary')}
            >
              <textarea
                rows={2}
                className={inputCls}
                value={draft.page.summary}
                maxLength={500}
                disabled={readOnly}
                onChange={(e) => setText({ summary: e.target.value })}
              />
            </Field>
            <Field
              label="Description"
              hint="Plain text. Blank lines start a new paragraph."
              {...validation.field('page.description')}
            >
              <textarea
                rows={5}
                className={inputCls}
                value={draft.page.description}
                maxLength={20000}
                disabled={readOnly}
                onChange={(e) => setText({ description: e.target.value })}
              />
            </Field>
          </div>
        </Card>
      )}

      {/* ------------------------------------------------------------ pictures */}
      {tab === 'page' && (
        <Card title="Pictures">
          {readOnly ? (
            <ReadOnlyPictures
              ids={[
                ...(draft.heroImageFileId ? [draft.heroImageFileId] : []),
                ...draft.galleryFileIds,
              ]}
              urls={urls}
            />
          ) : (
            <div className="flex flex-col gap-8">
              <GalleryPicker
                label="Hero picture"
                max={1}
                value={draft.heroImageFileId ? [draft.heroImageFileId] : []}
                onChange={(ids) => setDraft({ ...draft, heroImageFileId: ids[0] ?? null })}
                known={known}
              />
              <GalleryPicker
                label="Gallery"
                optional
                max={LIMITS.gallery}
                value={draft.galleryFileIds}
                onChange={(galleryFileIds) => setDraft({ ...draft, galleryFileIds })}
                known={known}
                {...validation.field('galleryFileIds', { within: true })}
              />
            </div>
          )}
        </Card>
      )}

      {/* ---------------------------------------------------------------- menu */}
      {tab === 'menu' && (
        <>
          <MenuEditor
            categories={draft.categories}
            onChange={(categories) => setDraft({ ...draft, categories })}
            readOnly={readOnly}
            urls={urls}
            known={known}
            field={validation.field}
            reveal={reveal}
          />
          <Card title="Menu note">
            <Field
              label="Fine print"
              hint="Shown under the menu on the public page. Optional."
              {...validation.field('page.menuNote')}
            >
              <input
                className={inputCls}
                value={draft.page.menuNote}
                maxLength={1000}
                disabled={readOnly}
                placeholder="Items are subject to daily availability."
                onChange={(e) => setText({ menuNote: e.target.value })}
              />
            </Field>
          </Card>
        </>
      )}

      {/* ------------------------------------------------------------- history */}
      {tab === 'page' && (
        <Card title="History">
          {history.isPending ? (
            <p className="m-0 text-base text-ink-muted">Loading revisions…</p>
          ) : history.isError ? (
            <p role="alert" className="m-0 text-base text-danger-text">
              {history.error instanceof ApiError
                ? history.error.message
                : 'The history could not be loaded.'}
            </p>
          ) : (
            <>
              <Table head={['Revision', 'Saved', '']}>
                {history.data.data.map((r) => (
                  <Row key={r.id} onClick={() => setRevisionNumber(r.revisionNumber)}>
                    <Cell className="font-mono text-md">#{r.revisionNumber}</Cell>
                    <Cell className="font-mono text-md">{stamp(r.createdAt)}</Cell>
                    <Cell>
                      <div className="flex flex-wrap gap-2">
                        {r.isPublished && (
                          <Chip className="border border-brand-edge/35 bg-brand-wash text-brand-strong">
                            Live
                          </Chip>
                        )}
                        {r.isDraft && <Chip>Current draft</Chip>}
                      </div>
                    </Cell>
                  </Row>
                ))}
              </Table>
              <Pager pagination={history.data.pagination} onPage={setHistoryPage} />
            </>
          )}
        </Card>
      )}

      <Modal
        open={previewing}
        onClose={() => setPreviewing(false)}
        title={dirty ? 'Preview · with unsaved edits' : 'Preview · saved draft'}
      >
        <CafePreview document={documentOf(draft)} urls={urls} />
      </Modal>

      <Modal
        open={revisionNumber !== null}
        onClose={() => setRevisionNumber(null)}
        title={`Revision #${revisionNumber ?? ''}`}
      >
        {revision.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading the revision…</p>
        ) : revision.isError ? (
          <p role="alert" className="m-0 text-base text-danger-text">
            {revision.error instanceof ApiError
              ? revision.error.message
              : 'That revision could not be read.'}
          </p>
        ) : (
          <>
            <p className="m-0 text-base text-ink-muted">
              Saved {stamp(revision.data.createdAt)}. Revisions are read-only.
            </p>
            <CafePreview document={revision.data.document} urls={revisionUrls} />
          </>
        )}
      </Modal>
    </div>
  );
}

/** The hero first, then the gallery, for someone who can look but not pick. */
function ReadOnlyPictures({ ids, urls }: { ids: string[]; urls: ReadonlyMap<string, string> }) {
  if (ids.length === 0) return <p className="m-0 text-base text-ink-muted">No pictures.</p>;
  return (
    <ul className="m-0 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-4">
      {ids.map((id, at) => (
        <li key={id} className="relative">
          {urls.get(id) ? (
            <img src={urls.get(id)} alt="" className="aspect-card w-full rounded-xl object-cover" />
          ) : (
            <span className="flex aspect-card w-full items-center justify-center rounded-xl border border-dashed border-hairline text-base text-ink-muted">
              Unavailable
            </span>
          )}
          {at === 0 && (
            <span className="absolute top-1.5 left-1.5 rounded-lg bg-panel px-2 py-0.5 font-mono text-xs font-semibold tracking-label text-on-panel uppercase">
              Hero
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}
